import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';

import useOutsideClick from '#/hooks/useClickOutside';
import useDebounce from '#/hooks/useDebounce';
import checkIcon from '#/assets/icons/check.svg';
import Loader from '#/components/Loader';
import styles from './styles.module.css';

import { useTranslation } from "react-i18next";


function Autocomplete({
  fetchItens,
  onSelect,
  title,
  type,
  icon,
  placeholder,
  selectedItens,
}) {
  const [isOpen, setOpen] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [itens, setItens] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [isLastPage, setLastPage] = useState(false);

  const listRef = useRef(null);
  const inputRef = useRef(null);

  const DEBOUNCE_INTERVAL = 500;

  const debouncedSearch = useDebounce(inputValue, DEBOUNCE_INTERVAL);
  useOutsideClick(listRef, inputRef, () => setOpen(false), isOpen);
  
  const { t } = useTranslation();

  useEffect(() => {
    if (debouncedSearch) fetch();
  }, [debouncedSearch]);

  useLayoutEffect(() => {
    const list = document.getElementById('listWrapper');
    if (listRef.current) {
      listRef.current.addEventListener('scroll', handleScroll);
      return () => list.removeEventListener('scroll', handleScroll);
    }
  });

  async function fetch() {
    try {
      setOpen(false);
      setLoading(true);
      const { data } = await fetchItens(inputValue);
      setOpen(true);
      setLoading(false);
      setItens(data.content.reverse());
      setCurrentPage(data.number + 1);
      setLastPage(data.last);
    } catch (error) {
      console.error('faield do get filters service');
    }
  }

  async function loadMore() {
    try {
      setLoading(true);
      const { data } = await fetchItens(inputValue, currentPage);
      setItens([...data.content.reverse(), ...itens]);
      setCurrentPage(data.number + 1);
      setLastPage(data.last);
      setLoading(false);
    } catch (error) {
      console.error('faield do load more filters');
    }
  }

  async function handleScroll() {
    const { current } = listRef;
    if (current.scrollTop + current.clientHeight === current.scrollHeight) {
      if (!isLastPage) loadMore();
    }
  }

  function handleSelect(item) {
    return (
      !item.added &&
      onSelect({
        id: item.id,
        name: item.name || item.companyName,
        type,
        added: true,
      })
    );
  }

  function renderName(name) {
    const NAME_LENGTH = 23;
    if (name.length >= NAME_LENGTH) {
      const newName = name.slice(0, NAME_LENGTH);
      return (
        <p className={styles.paragraph} title={name}>{`${newName} ...`}</p>
      );
    } else {
      return <p className={styles.paragraph}>{name}</p>;
    }
  }

  function renderList() {
    const selectedItensByType = selectedItens.filter(selected => {
      return selected.type === type;
    });
    const hash = new Map();
    [...selectedItensByType, ...itens].forEach(obj => {
      hash.set(obj.id, Object.assign(hash.get(obj.id) || {}, obj));
    });
    const mergedListArray = Array.from(hash.values());

    return mergedListArray.reverse().map(item => (
      <li
        key={item.id}
        onClick={() => handleSelect(item)}
        className={[styles.listItem, item.added ? styles.added : ''].join(' ')}
      >
        {renderName(item.name || item.companyName)}
        {item.added && (
          <img
            src={checkIcon}
            alt={t('ITEM_ALREADY_SELECTED')}
            className={styles.checkIcon}
          />
        )}
      </li>
    ));
  }

  function renderLoader() {
    return (
      isLoading && (
        <div className={styles.loader}>
          <Loader shape="small" />
        </div>
      )
    );
  }

  return (
    <div className={styles.wrapper}>
      <label className={styles.label} htmlFor="input">
        <img src={icon} alt={t('INPUT_ICON')} className={styles.icon} />
        {title}
      </label>
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        placeholder={placeholder}
        className={styles.input}
        onChange={e => setInputValue(e.target.value)}
        onFocus={() => itens.length && setOpen(true)}
      />
      {renderLoader()}
      {isOpen && !!inputValue && (
        <ul className={styles.listWrapper} ref={listRef} id="listWrapper">
          {itens.length ? (
            renderList(itens)
          ) : (
            <li className={[styles.listItem, styles.notFound].join(' ')}>
              {t('NO_ITEM_FOUND')}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

Autocomplete.propTypes = {
  fetchItens: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  type: PropTypes.string,
  icon: PropTypes.string,
  selectedItens: PropTypes.array,
};

export default Autocomplete;
